import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, X, SlidersHorizontal, LayoutGrid, List, Columns, AlertTriangle } from 'lucide-react';
import {
  DndContext, DragOverlay, useDroppable, useDraggable,
  PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { JobCard } from '../components/jobs/JobCard';
import { JobDetail } from '../components/jobs/JobDetail';
import { JobForm } from '../components/jobs/JobForm';
import { StatusBadge } from '../components/jobs/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Input';
import { useJobs } from '../hooks/useJobs';
import { useAuth } from '../context/AuthContext';
import { getJob, updateJobStatus, updateJob } from '../api/jobs';
import { getFieldStaff } from '../api/users';
import { formatJobNumber, STATUS_LABELS } from '../utils/constants';
import { isOverdueUnassigned, isOverdueAssigned } from '../utils/jobFilters';
import { todayStartIso } from '../utils/date';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const STATUS_OPTIONS = [
  { value: '', label: 'Tüm Durumlar' },
  { value: 'pending',    label: 'Bekliyor' },
  { value: 'assigned',   label: 'Atandı' },
  { value: 'on_the_way', label: 'Yolda' },
  { value: 'started',    label: 'Başladı' },
  { value: 'paused',     label: 'Beklemede' },
  { value: 'completed',  label: 'Tamamlandı' },
  { value: 'cancelled',  label: 'İptal' },
];

// Frontend dropdown — değerler backend SORT_OPTIONS whitelist'iyle senkron
// (Sprint 2.9 T1-A: smart/created_desc/created_asc/priority/job_number/status).
// 'smart' default backend behavior; frontend hep created_desc gönderir (default).
const SORT_OPTIONS = [
  { value: 'created_desc', label: 'En Yeni' },
  { value: 'created_asc',  label: 'En Eski' },
  { value: 'priority',     label: 'Öncelik' },
  { value: 'job_number',   label: 'İş Numarası' },
  { value: 'status',       label: 'Durum' },
];

const KANBAN_COLS = [
  { key: 'pending',    label: 'Bekliyor',    color: 'bg-slate-100 border-slate-300' },
  { key: 'assigned',   label: 'Atandı',      color: 'bg-blue-50 border-blue-200' },
  { key: 'on_the_way', label: 'Yolda',       color: 'bg-orange-50 border-orange-200' },
  { key: 'started',    label: 'Başladı',     color: 'bg-yellow-50 border-yellow-200' },
  { key: 'completed',  label: 'Tamamlandı',  color: 'bg-green-50 border-green-200' },
];

/**
 * Sprint 2.9 T1-B — urlFilter → backend query mapping.
 * Dashboard'dan navigate('/jobs?filter=<key>') ile gelen 6 değer için
 * backend `GET /api/jobs` filter param'larına çevirir.
 *
 * - active/completed: status CSV (T7-B B2.5)
 * - urgent: priority + exclude_statuses (mevcut backend param'ları)
 * - pending: status tek değer
 * - today_completed: status CSV + completed_after ISO (T1-A yeni param,
 *   T7-B today_start pattern — frontend MUTLAKA gönderir, KARAR 3)
 * - overdue: overdue_only=true (T1-A yeni param)
 */
function urlFilterToBackendFilters(urlFilter) {
  switch (urlFilter) {
    case 'active':           return { status: 'assigned,on_the_way,started' };
    case 'completed':        return { status: 'completed,approved' };
    case 'urgent':           return { priority: 'urgent', exclude_statuses: 'completed,approved,cancelled' };
    case 'pending':          return { status: 'pending' };
    case 'today_completed':  return { status: 'completed,approved', completed_after: todayStartIso() };
    case 'overdue':          return { overdue_only: 'true' };
    default:                 return {};
  }
}

export function JobsPage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'supervisor'].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({});     // status, priority (Filtre Paneli)
  const [search, setSearch] = useState('');        // backend search param'a türetilir
  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState('grid'); // grid | list | kanban
  const [sort, setSort] = useState('created_desc'); // backend sort param'a türetilir
  // Sprint 2.8.6 T7-D — backend pagination state (page externally controlled).
  // Kanban modu mevcut 20-cap'le kalır (Seçenek Y); pagination UI sadece grid/list.
  const [page, setPage] = useState(1);
  const [urlFilter, setUrlFilter] = useState(null); // active | completed | urgent | pending | today_completed | overdue

  // Sprint 2.9 T1-B — search/sort/urlFilter backend'e taşındı (capped-bug
  // ailesinin kapanış halkası). Tüm filtreler `backendFilters` obj'sinde
  // birleştirilip useJobs'a geçer; useJobs zaten `...filters` spread eder.
  const backendFilters = useMemo(() => ({
    ...filters,
    ...(search ? { search } : {}),
    sort,
    ...urlFilterToBackendFilters(urlFilter),
  }), [filters, search, sort, urlFilter]);

  const { jobs, pagination, loading, error, reload } = useJobs(backendFilters, page);

  const [createOpen, setCreateOpen] = useState(false);
  const [detailJob, setDetailJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editJob, setEditJob] = useState(null);

  // Sprint 2.9 T1-B — KARAR A: sort backend'e taşındı, semantik değişti
  // (sayfa içeriği global sıralamaya bağlı). Sort artık page=1 reset eder
  // — T7-D'nin "sort frontend, reset gerekmiyor" kararı GÜNCELLENDİ.
  // 6 wrap helper: filter/search/sort/urlFilter değişiminde page=1.
  const handleSearchChange = (v) => { setSearch(v); setPage(1); };
  const handleStatusFilterChange = (v) => { setFilters((f) => ({ ...f, status: v || undefined })); setPage(1); };
  const handlePriorityFilterChange = (v) => { setFilters((f) => ({ ...f, priority: v || undefined })); setPage(1); };
  const handleFiltersClear = () => { setFilters({}); setPage(1); };
  const handleUrlFilterClear = () => { setUrlFilter(null); setPage(1); };
  const handleSortChange = (v) => { setSort(v); setPage(1); };

  const URL_FILTER_LABELS = {
    active: 'Aktif İşler',
    completed: 'Tamamlanan İşler',
    urgent: 'Acil İşler',
    pending: 'Bekleyen İşler',
    today_completed: 'Bugün Tamamlanan',
    overdue: 'Gecikmiş İşler',
  };

  // URL'den gelen parametreleri işle
  useEffect(() => {
    const jobId = searchParams.get('jobId');
    if (jobId) {
      openDetail({ id: jobId });
      setSearchParams({}, { replace: true });
    }
    const filter = searchParams.get('filter');
    if (filter && URL_FILTER_LABELS[filter]) {
      setUrlFilter(filter);
      setPage(1); // T1-B: urlFilter değişimi page reset (mevcut state page>1 olabilir)
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (job) => {
    setDetailLoading(true);
    try {
      const data = await getJob(job.id);
      setDetailJob(data.job);
    } catch {
      setDetailJob(job);
    } finally {
      setDetailLoading(false);
    }
  };

  const now = new Date();

  // Sprint 2.9 T1-B — search/sort/urlFilter ARTIK BACKEND'de (backendFilters
  // useMemo → useJobs → GET /api/jobs sort/search/completed_after/overdue_only
  // param'ları). Frontend filtreleme bloğu KALDIRILDI (capped-bug ailesinin
  // kapanış halkası — DASHBOARD-COUNT T7-B + JOBS-PAGINATION T7-D + bu).
  //
  // ⚠ Gecikme uyarı banner'ları (overdueUnassigned/overdueAssigned) hâlâ
  // jobs[] üzerinde — page-scoped pre-existing davranış (JOBS-OVERDUE-COUNTER-1
  // Sprint 2.9 backlog'unda; /api/jobs/stats.overdue_* kullanılarak çözülecek).
  const overdueUnassigned = jobs.filter(j => isOverdueUnassigned(j, now));
  const overdueAssigned = jobs.filter(j => isOverdueAssigned(j, now));

  const activeFilters = Object.values(filters).filter(Boolean).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* URL filtre badge */}
      {urlFilter && (
        <div className="mb-4 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
          <SlidersHorizontal size={16} className="text-blue-500" />
          <span className="text-sm font-medium text-blue-700">{URL_FILTER_LABELS[urlFilter]} ({pagination.total})</span>
          <button onClick={handleUrlFilterClear}
            className="ml-auto text-blue-400 hover:text-blue-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">İşler</h1>
          <p className="text-slate-500 text-sm mt-0.5">{urlFilter ? `${pagination.total} iş gösteriliyor` : `${pagination.total} iş bulundu`}</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={16} />
            Yeni İş
          </Button>
        )}
      </div>

      {/* Gecikme Uyarıları */}
      {overdueUnassigned.length > 0 && (
        <div className="mb-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2.5 text-sm">
          <AlertTriangle size={15} className="shrink-0" />
          <span><strong>{overdueUnassigned.length} iş</strong> tarihi geçmiş ve atanmamış.</span>
        </div>
      )}
      {overdueAssigned.length > 0 && (
        <div className="mb-3 flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl px-4 py-2.5 text-sm">
          <AlertTriangle size={15} className="shrink-0" />
          <span><strong>{overdueAssigned.length} iş</strong> atanmış ama tarihi geçmiş.</span>
        </div>
      )}

      {/* Araç Çubuğu */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {/* Arama */}
        <div className="flex-1 min-w-48 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="İş no, başlık, müşteri, personel..."
            className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Filtre */}
        <Button variant={showFilters ? 'primary' : 'secondary'} size="md" onClick={() => setShowFilters(v => !v)}>
          <SlidersHorizontal size={14} />
          Filtrele
          {activeFilters > 0 && (
            <span className="bg-white text-blue-600 text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {activeFilters}
            </span>
          )}
        </Button>

        {/* Sıralama — KARAR A: sort backend, page=1 reset (handleSortChange wrap) */}
        <Select value={sort} onChange={e => handleSortChange(e.target.value)} className="w-40">
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>

        {/* Görünüm */}
        <div className="flex border border-slate-300 rounded-lg overflow-hidden">
          {[['grid', <LayoutGrid size={15} />], ['list', <List size={15} />], ['kanban', <Columns size={15} />]].map(([v, icon]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-2.5 py-2 transition-colors ${view === v ? 'bg-blue-500 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>

      {/* Filtre Paneli */}
      {showFilters && (
        <div className="bg-slate-50 rounded-xl border p-4 mb-4 flex flex-wrap gap-3 items-end">
          <Select label="Durum" value={filters.status || ''} onChange={e => handleStatusFilterChange(e.target.value)}>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Select label="Öncelik" value={filters.priority || ''} onChange={e => handlePriorityFilterChange(e.target.value)}>
            <option value="">Tüm Öncelikler</option>
            <option value="urgent">🔴 Acil</option>
            <option value="normal">🔵 Normal</option>
            <option value="low">⚫ Düşük</option>
          </Select>
          {activeFilters > 0 && (
            <Button variant="ghost" size="sm" onClick={handleFiltersClear}>
              <X size={13} /> Temizle
            </Button>
          )}
        </div>
      )}

      {/* İçerik */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-36 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">{error}</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-slate-300 text-5xl mb-3">📋</div>
          <p className="text-slate-500">İş bulunamadı</p>
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map(job => <JobCard key={job.id} job={job} onClick={openDetail} />)}
        </div>
      ) : view === 'list' ? (
        <ListView jobs={jobs} onSelect={openDetail} />
      ) : (
        <KanbanView jobs={jobs} onSelect={openDetail} onJobMoved={reload} />
      )}

      {/* Sayfalama (Sprint 2.8.6 T7-D — grid/list view'lerinde; kanban scope dışı, Seçenek Y) */}
      {view !== 'kanban' && !loading && pagination.total > pagination.limit && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Önceki
          </Button>
          <span className="text-sm text-slate-500">
            Sayfa <strong className="text-slate-700">{pagination.page}</strong> / {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= Math.ceil(pagination.total / pagination.limit)}
            onClick={() => setPage((p) => p + 1)}
          >
            Sonraki →
          </Button>
        </div>
      )}

      {/* Modaller */}
      <Modal open={!!detailJob} onClose={() => setDetailJob(null)} title={detailJob?.title || ''} size="lg">
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : detailJob ? (
          <JobDetail
            job={detailJob}
            onClose={() => setDetailJob(null)}
            onEdit={(job) => { setEditJob(job); setDetailJob(null); }}
            onRefresh={() => { reload(); setDetailJob(null); }}
          />
        ) : null}
      </Modal>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Yeni İş Oluştur" size="lg">
        <JobForm onSuccess={() => { setCreateOpen(false); reload(); }} onCancel={() => setCreateOpen(false)} />
      </Modal>

      <Modal open={!!editJob} onClose={() => setEditJob(null)} title="İşi Düzenle" size="lg">
        {editJob && (
          <JobForm
            job={editJob}
            onSuccess={() => { setEditJob(null); reload(); }}
            onCancel={() => setEditJob(null)}
          />
        )}
      </Modal>
    </div>
  );
}

// ── Liste Görünümü ──────────────────────────────────────────────────────────
function ListView({ jobs, onSelect }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-slate-50">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">No</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Başlık</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">Müşteri</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">Personel</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Durum</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">Tarih</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {jobs.map(job => (
            <tr key={job.id} onClick={() => onSelect(job)} className="hover:bg-slate-50 cursor-pointer transition-colors">
              <td className="px-4 py-3 text-xs font-mono text-slate-400 whitespace-nowrap">
                {job.job_number ? formatJobNumber(job.job_number) : '—'}
              </td>
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-slate-800 line-clamp-1">{job.title}</p>
                {job.address && <p className="text-xs text-slate-400 truncate max-w-xs">{job.address}</p>}
              </td>
              <td className="px-4 py-3 text-sm text-slate-600 hidden md:table-cell">
                {job.customer_name || '—'}
              </td>
              <td className="px-4 py-3 text-sm text-slate-600 hidden md:table-cell">
                {job.assigned_to_name || <span className="text-slate-400">Atanmamış</span>}
              </td>
              <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap hidden lg:table-cell">
                {job.created_at && format(new Date(job.created_at), 'd MMM yyyy', { locale: tr })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Kanban Görünümü ─────────────────────────────────────────────────────────

function getInvalidMoveMessage(from, to) {
  const order = { pending: 0, assigned: 1, on_the_way: 2, started: 3, completed: 4 };
  if (from === 'completed') return 'Tamamlanan iş geri alınamaz.';
  if (order[to] !== undefined && order[from] !== undefined && order[to] < order[from]) {
    return 'Geriye dönük durum değişikliği yapılamaz.';
  }
  if (from === 'pending' && to !== 'assigned') return 'Önce personel ataması yapmalısınız.';
  if (from === 'assigned' && (to === 'started' || to === 'completed')) return "Önce 'Yolda' durumuna almalısınız.";
  if (from === 'on_the_way' && to === 'completed') return "Önce 'Başladı' durumuna almalısınız.";
  return null;
}

function KanbanCardContent({ job }) {
  return (
    <>
      {job.job_number && (
        <span className="text-xs font-mono text-slate-400">{formatJobNumber(job.job_number)}</span>
      )}
      <p className="text-sm font-medium text-slate-800 line-clamp-2 mt-0.5">{job.title}</p>
      {job.customer_name && (
        <p className="text-xs text-slate-400 mt-1 truncate">{job.customer_name}</p>
      )}
      {job.assigned_to_name && (
        <p className="text-xs text-slate-500 mt-1 truncate">👤 {job.assigned_to_name}</p>
      )}
    </>
  );
}

function DraggableCard({ job, onSelect }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: job.id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onSelect(job)}
      className={`bg-white rounded-lg border border-slate-200 p-3 cursor-grab active:cursor-grabbing hover:shadow-sm transition-all select-none ${isDragging ? 'opacity-40' : ''}`}
    >
      <KanbanCardContent job={job} />
    </div>
  );
}

function DroppableColumn({ col, jobs, onSelect, isOver }) {
  const { setNodeRef } = useDroppable({ id: col.key });
  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 rounded-xl border p-3 transition-colors ${
        isOver ? 'ring-2 ring-blue-400 ring-offset-1 bg-blue-50 border-blue-300' : col.color
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-slate-700">{col.label}</span>
        <span className="text-xs bg-white/70 text-slate-500 px-2 py-0.5 rounded-full font-medium">
          {jobs.length}
        </span>
      </div>
      <div className="space-y-2 min-h-[40px]">
        {jobs.map(job => (
          <DraggableCard key={job.id} job={job} onSelect={onSelect} />
        ))}
        {jobs.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4">Boş</p>
        )}
      </div>
    </div>
  );
}

// Hata popup'ı
function ErrorPopup({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
            <AlertTriangle size={16} className="text-red-500" />
          </div>
          <p className="text-slate-700 text-sm leading-relaxed">{message}</p>
        </div>
        <Button onClick={onClose} className="w-full justify-center">Tamam</Button>
      </div>
    </div>
  );
}

// Personel atama modalı (pending → assigned)
function AssignStaffModal({ job, onConfirm, onCancel }) {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    getFieldStaff()
      .then(d => setStaffList(d.users))
      .catch(() => setError('Personel listesi yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      await onConfirm(selected);
    } catch (err) {
      setError(err.response?.data?.error || 'Atama yapılamadı');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
        <p className="font-semibold text-slate-800 mb-1">Personel Ata</p>
        <p className="text-xs text-slate-500 mb-4 line-clamp-2">{job.title}</p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            Yükleniyor...
          </div>
        ) : (
          <Select value={selected} onChange={e => setSelected(e.target.value)} className="mb-3">
            <option value="">— Personel seçin —</option>
            {staffList.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        )}

        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

        <div className="flex gap-2">
          <Button
            onClick={handleConfirm}
            loading={saving}
            disabled={!selected || loading}
            className="flex-1 justify-center"
          >
            Ata
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={saving} className="flex-1 justify-center">
            İptal
          </Button>
        </div>
      </div>
    </div>
  );
}

function KanbanView({ jobs, onSelect, onJobMoved }) {
  const [localJobs, setLocalJobs]   = useState(jobs);
  const [activeJob, setActiveJob]   = useState(null);
  const [overId, setOverId]         = useState(null);
  const [assignModal, setAssignModal] = useState(null); // { job }
  const [errorMsg, setErrorMsg]     = useState('');

  useEffect(() => { setLocalJobs(jobs); }, [jobs]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = ({ active }) => {
    setActiveJob(localJobs.find(j => j.id === active.id) || null);
  };

  const handleDragOver = ({ over }) => {
    setOverId(over?.id || null);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveJob(null);
    setOverId(null);
    if (!over) return;

    const job = localJobs.find(j => j.id === active.id);
    const targetStatus = over.id;
    if (!job || job.status === targetStatus) return;

    // Özel: Bekliyor → Atandı — personel seçim modalı aç
    if (job.status === 'pending' && targetStatus === 'assigned') {
      setAssignModal({ job });
      return;
    }

    // Geçersiz geçiş kontrolü
    const errMsg = getInvalidMoveMessage(job.status, targetStatus);
    if (errMsg) {
      setErrorMsg(errMsg);
      return;
    }

    // Geçerli geçiş — optimistik güncelle
    const prev = localJobs;
    setLocalJobs(list => list.map(j => j.id === job.id ? { ...j, status: targetStatus } : j));

    updateJobStatus(job.id, { status: targetStatus })
      .then(() => onJobMoved?.())
      .catch(() => setLocalJobs(prev));
  };

  const handleAssignConfirm = async (staffId) => {
    const job = assignModal.job;
    // updateJob pending → assigned + personel ataması (backend otomatik status=assigned yapar)
    await updateJob(job.id, { assigned_to: staffId });
    setLocalJobs(list => list.map(j =>
      j.id === job.id ? { ...j, status: 'assigned' } : j
    ));
    setAssignModal(null);
    onJobMoved?.();
  };

  return (
    <>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {KANBAN_COLS.map(col => (
            <DroppableColumn
              key={col.key}
              col={col}
              jobs={localJobs.filter(j => j.status === col.key)}
              onSelect={onSelect}
              isOver={overId === col.key}
            />
          ))}
        </div>
        <DragOverlay>
          {activeJob && (
            <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-xl w-72 rotate-2 opacity-95">
              <KanbanCardContent job={activeJob} />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {assignModal && (
        <AssignStaffModal
          job={assignModal.job}
          onConfirm={handleAssignConfirm}
          onCancel={() => setAssignModal(null)}
        />
      )}

      <ErrorPopup message={errorMsg} onClose={() => setErrorMsg('')} />
    </>
  );
}
